import React, {
  useState, useMemo, useRef,
} from 'react';
import {
  Button, Popup, Icon, Progress, Label, Message, Divider,
} from 'semantic-ui-react';
import { Link, withRouter } from 'react-router-dom';
import useTheme from 'theme/useTheme';
import util from 'utils/utils';
import moment from 'moment';
import styled from 'styled-components';
import { useAppSelector } from 'store';

import { IdeaAssessmentReport, IdeaLikeHandler } from 'components/lib/Ideas';
import Emoji from 'components/lib/Emoji/Emoji';
import TagModalChooser from 'components/lib/Choosers/Tags/TagModalChooser';
import SelectableDropdown from 'components/lib/SelectableDropdown';

import {
  SelectIdeaChip, IdeaListCard, IdeaCoverImage, IdeaInfo, StatusLabel, IdeaMeta, IdeaUpdateStatus, IdeaActions, IdeaSocialChip, IdeaTeamChip, IdeaPinnedIcon, AuthorPopup, Author, AssessmentSummary, CompanyProfileImage, IdeaProject,
} from './styles';

const StyledCell = styled.td`
  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 100%;
    min-width: 100%;
    background: #fff;

    ${({ isPinned, right, left }) => (isPinned ? `
      border-top: 1px solid #048C85;
      border-bottom: 1px solid #048C85;
      ${right ? 'border-right: 1px solid #048C85;' : ''}
      ${left ? 'border-left: 1px solid #048C85;' : ''}
    ` : '')}
    width: 100%;
    height: 55px;

    ${({ left }) => (left ? `
      border-top-left-radius: 5px;
      border-bottom-left-radius: 5px;
    ` : '')}

    ${({ right }) => (right ? `
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
    ` : '')}
  }
`;

const StyledProgress = styled(Progress)`
  &&&& {
    > div {
      height: 0.25rem;
      ${({ isPinned }) => (isPinned ? 'border-radius: 0;' : '')}
    }
  }
`;

const Cell = ({ children, containerStyle = {}, ...props }) => (
  <StyledCell {...props}>
    <div style={containerStyle}>
      {children}
    </div>
  </StyledCell>
);

const IdeaCardListView = (props) => {
  const {
    idea,
    isSelected,
    onSelect,
    challenge: propChallenge,
    pinIdea,
    usedVotes,
    voteForIdea,
    removeAssessors,
    ideaActions,
    invitations,
    setOpenToComments,
    setPreviewOpen,
    addTag,
    unTag,
    disableManagement,
    showChallenge,
    history,
    onRemoveExternalInvitation,
    fetchIdeas,
    hideActions,
    assessmentReportStyle,
    hasPinnedIdea,
    // Update project lane
    allChallengeProjectLanes,
    updateProjectLane,
  } = props;
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);
  const ideaOrder = useAppSelector((state) => state.challenges.ideaOrder);

  const { collaborators = [], impacts = [] } = idea;
  const challenge = propChallenge || idea.ownerChallenge;
  const canManage = (util.canManageChallenge(user, challenge) && !disableManagement);
  const rowRef = useRef(null);

  const hasAssessments = useMemo(() => idea.assessments?.length > 0 || idea?.invitedExternalAssessors?.length > 0, [idea]);
  const userAssessment = useMemo(() => {
    const hasAssessment = (idea.assessments || []).find((assessment) => assessment.user === user._id);
    return { isAssessor: !!hasAssessment, isComplete: !hasAssessment || hasAssessment?.isSubmitted };
  }, [idea, user]);
  const assessmentHasConflictOfInterest = useMemo(() => idea.assessmentHasConflictOfInterest && util.canManageChallenge(user, challenge), [idea, user, challenge]);

  const submittedAssessments = idea.assessments?.filter((a) => a.isSubmitted).length;
  const totalAuthors = (collaborators.length + 1);

  const completenessPercentage = (idea.completeness || 0) * 100;
  const ideaComplete = idea.completeness && (idea.completeness || 1);
  const showCompleteness = (completenessPercentage && !idea.isSubmitted);

  const { projectManagement = {} } = idea;
  const { boards = [] } = projectManagement;
  const pmBoardObject = boards.length ? (boards.find((b) => b.forId === propChallenge?._id)) : null;
  const userIsAssigned = useMemo(() => {
    const isAssigned = (pmBoardObject?.assignees || []).find((assigneeId) => assigneeId === user._id);
    return !!isAssigned;
  }, [user, pmBoardObject]);

  const isFullSize = !theme.sizes.isMobile;

  const [tagsOpen, setTagsOpen] = useState(false);
  const openProps = tagsOpen ? { open: tagsOpen } : {};

  const {
    activityInfo = {},
    createdAt = 0,
  } = idea;

  const { lastActivityAt = 0 } = activityInfo;

  const momentLastActivityAt = moment(lastActivityAt);
  const momentCreatedAt = moment(createdAt);
  const threeDaysAgo = moment().subtract(3, 'days').valueOf();
  const oneDayAgo = moment().subtract(1, 'day').valueOf();
  const isNewIdea = (momentCreatedAt.valueOf() > threeDaysAgo);
  const isHotIdea = (momentLastActivityAt.valueOf() > oneDayAgo);

  const pendingInvitations = invitations && invitations.filter((i) => i?.forIdeas && i.forIdeas.includes(idea._id));

  const activeImpactSort = useMemo(() => (!!propChallenge && (idea?.impacts?.find((impact) => (`impact+${impact.impactId}` === ideaOrder)))), [ideaOrder, idea, propChallenge]);

  const IdeaIdentity = useMemo(() => {
    if (idea.businessName) return idea.businessName;
    return <Link to={`/users/${idea.user}`}>{util.getProfileName(idea?.owner)}</Link>;
  }, [idea]);

  const annotations = useMemo(() => {
    const notices = [];
    const problems = {...(idea.problems ?? {})};
    if (userAssessment?.isAssessor && !userAssessment?.isComplete) {
      notices.push('You are an assessor for this idea and still need to assess it.');
    }
    if (userIsAssigned) {
      notices.push('You are assigned to this idea on the project board, make sure to update it with relevant changes.');
    }
    if (assessmentHasConflictOfInterest) {
      problems['hasConflictOfInterest'] = true;
    }
    const noticeCount = notices.length;
    const problemCount = Object.keys(problems).length;

    return {
      notices,
      problems,
      count:noticeCount + problemCount,
      hasNotices: noticeCount > 0,
      hasProblems: problemCount > 0,
    }
  }, [userAssessment, userIsAssigned, assessmentHasConflictOfInterest, idea.problems]);

  // Calculate the width of the progress bar
  let progressWidth = rowRef.current?.clientWidth || 0;
  if (isFullSize && canManage) {
    progressWidth -= 40; // adjust for checkbox (35px width + 5px padding)
  }
  if (hasPinnedIdea || canManage) {
    progressWidth -= 35; // adjust for pin icon (35px)
  }
  progressWidth -= 1; // Adjust for 1px border
  progressWidth = Math.max(progressWidth, 0);

  return (
    <>
      <IdeaListCard isPinned={idea.isPinned} canManage={canManage} ref={rowRef}>
        {(isFullSize && canManage)
          ? (
            <>
              <td style={{ paddingRight: 5 }}>
                <SelectIdeaChip isSelected={isSelected} onClick={() => onSelect(idea)}>
                  <Icon name='check' />
                </SelectIdeaChip>
              </td>
            </>
          ) : null
        }

        {(idea.isPinned || canManage)
          ? (
            <Popup
              on='hover'
              trigger={(
                <Cell left isPinned={idea.isPinned}>
                  <IdeaPinnedIcon
                    isPinned={idea.isPinned}
                    canManage={canManage}
                    onClick={() => { if (pinIdea && canManage) pinIdea(idea._id, !idea.isPinned); }}
                    title='Pin idea'
                    aria-label={idea.isPinned ? 'Idea is pinned' : 'Idea not pinned'}
                  >
                    <Icon name='thumbtack' />
                  </IdeaPinnedIcon>
                </Cell>
              )}
              content='Pinned idea'
              disabled={canManage}
            />
          ) : <td />
        }
        <Cell left={!canManage && !idea.isPinned} containerStyle={{ paddingLeft: 8, position: 'relative' }} isPinned={idea.isPinned}>
          {showCompleteness
            ? (
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                minWidth: progressWidth,
              }}>
                <Popup
                  content={`Idea is ${Math.round(completenessPercentage)}% complete`}
                  position='top center'
                  context={rowRef}
                  trigger={
                    <StyledProgress
                      size='tiny'
                      isPinned={idea.isPinned}
                      indicating={!ideaComplete}
                      success={ideaComplete}
                      percent={completenessPercentage} />
                  }
                />
              </div>
            ) : null}

          <IdeaCoverImage coverImage={util.ideaCoverImage(idea)} onClick={() => setPreviewOpen(idea._id)} />
        </Cell>
        <Cell isPinned={idea.isPinned}>
          <IdeaInfo canManage={canManage}>
            <div>
              {(isNewIdea || isHotIdea)
                ? (
                  <Popup
                    on='hover'
                    hoverable
                    trigger={(
                      <Label
                        size='mini'
                        style={{ backgroundColor: isNewIdea ? '#18AE9f' : '#BF2713', color: '#fff', marginRight: 4 }}
                      >
                        {isNewIdea ? 'NEW!' : 'HOT!'}
                      </Label>
                    )}
                    content={
                      <div>
                        <p>Last activity was {momentLastActivityAt.fromNow()}.</p>
                        <p>{momentLastActivityAt.format('llll')}</p>
                      </div>
                    }
                  />
                ) : null}
              <h4 onClick={() => setPreviewOpen(idea._id)}>{idea.children ? <Icon name='sitemap' size='small' /> : null}{idea.name}</h4>
              {idea.summarisedContent
                ? (
                  <Popup
                    trigger={(
                      <Icon
                        name='info circle'
                        style={{ cursor: 'pointer', marginLeft: 5, height: 22 }}
                      />
                    )}
                    content={(
                      <div>
                        <p><b>AI Content summary</b></p>
                        <p>{idea.summarisedContent}</p>
                      </div>
                    )}
                    on='hover'
                  />
                ) : null
              }
            </div>
            <div>
              {(isFullSize)
                ? (
                  <>
                    {(idea.businessName)
                      ? (
                        <>
                          {idea.ownerIdeaBusinessProfile?.profile?.imageUrl
                            ? (
                              <CompanyProfileImage imageUrl={idea.ownerIdeaBusinessProfile?.profile?.imageUrl} />
                            ) : (
                              <Icon name='building' style={{ height: 20, width: 17.5 }} />
                            )
                          }
                        </>
                      ) : (
                        <IdeaTeamChip
                          active={totalAuthors > 1}
                          size='mini'
                        >
                          <Icon name='group' />
                          <span>{totalAuthors}</span>
                        </IdeaTeamChip>
                      )
                    }
                  </>

                ) : null
              }
              {(idea?.owner)
                && (
                  <span>
                    {IdeaIdentity}
                    {((collaborators.length || !!idea.businessName) && idea.authors && isFullSize)
                      ? (
                        <>
                          {' + '}
                          <Popup
                            on='click'
                            trigger={(
                              <Link to='' onClick={(e) => e.preventDefault()}>{util.pluralise((idea.businessName ? (collaborators.length + 1) : collaborators.length), 'other', 'others')}</Link>
                            )}
                            content={(
                              <AuthorPopup>
                                {idea?.authors.filter((a) => !!idea.businessName || (a._id !== idea.user)).map((a, i) => (
                                  <Author key={i} to={`/users/${a._id}`}>{a?.profile?.fullName}</Author>
                                ))}
                              </AuthorPopup>
                            )}
                          />
                        </>
                      ) : null
                    }

                  </span>
                )
              }
            </div>
          </IdeaInfo>
          <div style={{
            width: '1px',
            borderRight: '0.5px solid gray',
            height: '40px',
          }} />
        </Cell>
        {(showChallenge && isFullSize && challenge)
          ? (
            <Cell>
              <IdeaUpdateStatus style={{ width: '100%', paddingLeft: 5 }}>
                <Icon name='target' />
                {challenge.name
                  ? (
                    <Link to={`/challenges/${challenge._id}`}>{challenge.name}</Link>
                  ) : (
                    <span>Challenge deleted</span>
                  )
                }
              </IdeaUpdateStatus>
            </Cell>
          ) : null
        }
        {isFullSize
          && (
            <Cell right={hideActions} isPinned={idea.isPinned}>
              <IdeaMeta>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Popup
                    disabled={!idea.stamps?.length || idea.stamps?.length <= 5}
                    trigger={
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        {(idea.stamps?.length > 0 && isFullSize)
                          ? (
                            idea.stamps
                              .slice(0, 5)
                              .map((stamp, i) => <Emoji
                                key={i}
                                emoji={{ id: stamp }}
                                size={15}
                              />)
                          ) : null}
                        {idea.stamps?.length > 5
                          ? (
                            <div style={{
                              fontSize: 10, lineHeight: '10px',
                            }}>
                              <span>+{idea.stamps.length - 5} others</span>
                            </div>
                          ) : null}
                      </div>
                    }
                    content={
                      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                        {(idea.stamps?.length > 0 && isFullSize) ? idea.stamps.map((stamp, i) => <Emoji
                          key={i}
                          emoji={{ id: stamp }}
                          size={15}
                        />) : null}
                      </div>
                    }
                  />

                </div>
                <IdeaUpdateStatus>
                  {idea.isSubmitted
                    ? (
                      <><StatusLabel color='green' /><span>Submitted {moment(idea.submittedAt).format('DD/MM/YY')}</span></>
                    ) : (
                      <><StatusLabel color='yellow' /><span>Updated {moment(idea.updatedAt).format('DD/MM/YY')}</span></>
                    )
                  }
                  {allChallengeProjectLanes?.length
                    ? (
                      <Popup
                        style={{ minWidth: 400 }}
                        hoverable
                        on='click'
                        trigger={(
                          <IdeaProject isAssigned={userIsAssigned}>
                            <span>
                              <Icon name='compass' onClick={() => history.push(`/challenges/${challenge?._id}/board`)} />
                            </span>
                            <span className='lane-name'>{pmBoardObject?.laneObject?.name ? `Lane: ${pmBoardObject.laneObject.name}` : 'Not on board'}</span>
                          </IdeaProject>
                        )}
                        content={(
                          <div>
                            <h5 style={{ margin: 0 }}>Project Management</h5>
                            <span style={{ marginBottom: 10, display: 'block' }}>
                              {pmBoardObject?.laneObject?.name
                                ? `This idea is added to the challenge's project board on the '${pmBoardObject.laneObject.name}' lane.`
                                : 'This idea is not assigned to a challenge project board lane.'
                              }
                              {' You can change this in the dropdown below.'}
                            </span>
                            <SelectableDropdown
                              single
                              placeholder='Add this idea to a project board lane'
                              value={pmBoardObject?.lane}
                              clearable={false}
                              options={[
                                {
                                  text: propChallenge?.projectBoard?.defaultLane?.name ?? 'Default',
                                  value: 'default',
                                },
                                ...allChallengeProjectLanes.map((lane) => ({
                                  text: lane.name,
                                  value: lane._id,
                                })),
                              ]}
                              onChange={(value) => updateProjectLane(idea._id, value)}
                            />
                            {userIsAssigned ? (
                              <Message>You are assigned to this idea, make sure to update it with relevant changes on the project board.</Message>
                            ) : null}
                            {/* boards.map(b => b.isEnabled ? (
                            <span>{b.name}</span>
                          ) : null) */}
                          </div>
                        )}
                      />
                    ) : null
                  }
                </IdeaUpdateStatus>

                <Popup
                  style={{ zIndex: 990, ...(assessmentReportStyle || {}) }}
                  disabled={!hasAssessments}
                  trigger={(
                    hasAssessments
                      ? (
                        <div>
                          <AssessmentSummary isComplete={userAssessment?.isComplete}>
                            {!userAssessment?.isComplete ? (
                              <span>
                                <Icon name="wait" />
                              </span>
                            ) : null}
                            <span>{Math.round(idea.assessmentSubmittedScore || 0)}% from {submittedAssessments || 0}/{idea.assessments?.length || 0} assessors</span>
                            {pendingInvitations?.length > 0 ? (
                              <span> ({pendingInvitations?.length} pending)</span>
                            ) : null}
                          </AssessmentSummary>
                        </div>
                      ) : null
                  )}
                  on='click'
                  basic
                  content={(
                    <div style={{ width: 500 }}>
                      <IdeaAssessmentReport fetchIdeas={fetchIdeas} challenge={challenge} idea={idea} removeAssessors={removeAssessors} onRemoveExternalInvitation={onRemoveExternalInvitation} />
                      {!userAssessment?.isComplete ? (
                        <Message>You were assigned as an assessor for this idea and still need to assess it.</Message>
                      ) : null}
                    </div>
                  )}
                />
              </IdeaMeta>
            </Cell>
          )}

        {!hideActions
          ? (
            <Cell right isPinned={idea.isPinned}>
              <IdeaActions>
                {(annotations.count > 0)
                  ? (
                    <Popup
                      trigger={
                        <IdeaSocialChip
                          actioned
                          warning={annotations.hasProblems}
                        >
                          <Icon name={annotations.hasProblems ? 'warning sign' : 'info circle'} />
                          <span>{annotations.count}</span>
                        </IdeaSocialChip>
                      }
                      content={(
                        <div>
                          {annotations.problems?.usersNotInAudience
                            ? (
                              <div>
                                <h5>This ideas has assignees or assessors that aren't currently in the challenge audience:</h5>
                                {annotations.problems.usersNotInAudience.assessors?.length
                                  ? (
                                    <div style={{ marginBottom: 10 }}>
                                      The following assessors are not currently in the audience: <b>{idea.problems.usersNotInAudience.assessorUsers.map((assessor) => assessor.profile.fullName).join(', ')}</b>
                                    </div>
                                  ) : null
                                }

                                {annotations.problems.usersNotInAudience.assignees?.length
                                  ? (
                                    <div>
                                      The following project assignees are not currently in the audience: <b>{idea.problems.usersNotInAudience.assigneeUsers.map((assignee) => assignee.profile.fullName).join(', ')}</b>
                                    </div>
                                  ) : null
                                }
                              </div>
                            ) : null
                          }

                          {annotations.problems.hasConflictOfInterest ? (
                            <h5>An assessor in this idea has declared a conflict of interest.</h5>
                          ) : null}

                          {(annotations.notices.length > 0 && !!annotations.problems)
                            ? (
                              <Divider />
                            ) : null
                          }

                          {annotations.notices.length > 0
                            ? (
                              <div>
                                <h5>You should consider:</h5>
                                <ol style={{ paddingLeft: 20 }}>
                                  {annotations.notices.map((notice) => (
                                    <li>{notice}</li>
                                  ))}
                                </ol>
                              </div>
                            ) : null
                          }
                        </div>
                      )}
                    />
                  ) : null
                }

                {(activeImpactSort && isFullSize)
                  ? (
                    <IdeaSocialChip backgroundColor='#E03997'>
                      <Icon name={activeImpactSort.ownerImpact.icon} style={{ marginLeft: 1 }}/>
                      <span>{activeImpactSort.value}</span>
                    </IdeaSocialChip>
                  ) : null
                }

                {isFullSize
                  ? (
                    <Popup on='hover' hoverable
                      style={{ zIndex: 1000 }}
                      trigger={
                        <IdeaSocialChip>
                          <Icon name='hashtag' />
                          <span>{idea.tags?.length || 0}</span>
                        </IdeaSocialChip>
                      }
                      content={<div>
                        <h4>Tags</h4>
                        <TagModalChooser
                          trigger={<Button compact icon='hashtag' circular content='Edit tags' size='tiny' style={{ marginLeft: 3 }} />}
                          tags={idea.fullTags}
                          availableTags={challenge.tagList}
                          addTag={addTag}
                          removeTag={unTag}
                          canEdit={canManage}
                          onStateChange={(state) => setTagsOpen(state)}
                        />
                        {impacts?.length > 0
                          && <>
                            <h4>Impact</h4>
                            <IdeaActions justify='center'>
                              {impacts.filter((i) => i?.ownerImpact && i?.value && i.ownerImpact?.type !== 'dropdown').map((impact) => (
                                <IdeaSocialChip actionable={false}>
                                  <Icon name={impact.ownerImpact.icon} />
                                  <span>{impact.value}</span>
                                </IdeaSocialChip>
                              ))}
                            </IdeaActions>
                            <div style={{ width: '100%', height: '100%' }}>
                              {impacts.filter((i) => i?.ownerImpact && i?.value && i.ownerImpact?.type === 'dropdown').map((impact) => (
                                <div>
                                  <Icon name={impact.ownerImpact.icon} />
                                  <span style={{
                                    fontSize: '1em', fontWeight: 'bold', textOverflow: 'ellipsis', maxWidth: '100%', color: '#505050',
                                  }}>{impact.value}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        }
                      </div>}
                      {...openProps}
                    />
                  ) : null
                }

                {(!challenge?.preventIdeaComments)
                  ? (
                    <IdeaSocialChip
                      onClick={() => {
                        setOpenToComments(true);
                        setPreviewOpen(idea._id);
                      }}
                    >
                      <Icon name='conversation' />
                      <span>{idea.commentCount || 0}</span>
                    </IdeaSocialChip>
                  ) : null
                }

                {((canManage || challenge?.votingVisibility === 'users'))
                  ? (
                    <IdeaLikeHandler
                      idea={idea}
                      challenge={challenge}
                      onLikeIdea={voteForIdea}
                      trigger={(
                        <IdeaSocialChip
                          actionable={!usedVotes && idea.votedFor}
                          actioned={idea.votedFor}
                        >
                          <Icon name='thumbs up' />
                          {canManage || challenge?.voteCountVisibility === 'users'
                            ? (
                              <span>{idea.voteCount || 0}</span>
                            ) : null
                          }
                        </IdeaSocialChip>
                      )}
                    />


                  ) : null
                }

                {canManage
                  ? (
                    <Popup
                      on={theme.sizes.isComputer ? 'hover' : 'click'}
                      hoverable
                      trigger={(
                        <IdeaSocialChip iconOnly title='Idea settings'>
                          <Icon name='settings' />
                        </IdeaSocialChip>
                      )}
                      style={{ padding: 5 }}
                      content={ideaActions([idea])}
                      position='top right'
                    />
                  ) : null
                }
              </IdeaActions>
            </Cell>
          ) : null
        }
      </IdeaListCard>
      <tr height="4" />
    </>
  );
};

export default withRouter((IdeaCardListView));
